import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Monitor, Plus, Play, Square, Radio, Copy, Check,
  Tv, Users, Clock, Trash2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * TournamentStreamManager - Manages court-level streams for tournament mode
 * Allows creating court streams, activating games, and managing the stream flow
 */
export function TournamentStreamManager({ eventId, eventName, token }) {
  const [courts, setCourts] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCourtName, setNewCourtName] = useState('Court 1');
  const [newCourtNumber, setNewCourtNumber] = useState(1);
  const [newPlatform, setNewPlatform] = useState('custom');
  const [newRtmpUrl, setNewRtmpUrl] = useState('');
  const [newStreamKey, setNewStreamKey] = useState('');
  const [newPlaybackUrl, setNewPlaybackUrl] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);
  const [activatingGame, setActivatingGame] = useState(null);
  const [editingCourt, setEditingCourt] = useState(null);
  const [editCourtForm, setEditCourtForm] = useState({ court_name: '', rtmp_url: '', stream_key: '', playback_url: '' });

  // Fetch courts and games
  const fetchData = async () => {
    try {
      // Fetch courts for this event
      const courtsRes = await fetch(`${API_URL}/api/court-streams/event/${eventId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (courtsRes.ok) {
        const courtsData = await courtsRes.json();
        setCourts(courtsData);
      }

      // Fetch games for this event
      const gamesRes = await fetch(`${API_URL}/api/engine/games?event_id=${eventId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (gamesRes.ok) {
        const gamesData = await gamesRes.json();
        setGames(gamesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchData();
      // Refresh every 30 seconds
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [eventId, token]);

  // Create new court stream
  const createCourtStream = async () => {
    try {
      const body = {
        event_id: eventId,
        court_name: newCourtName,
        court_number: newCourtNumber,
        platform: newPlatform,
      };
      // Include custom stream config if provided
      if (newRtmpUrl) body.rtmp_url = newRtmpUrl;
      if (newStreamKey) body.stream_key = newStreamKey;
      if (newPlaybackUrl) body.playback_url = newPlaybackUrl;

      const res = await fetch(`${API_URL}/api/court-streams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(`${newCourtName} created successfully`);
        setCreateDialogOpen(false);
        setNewCourtNumber(prev => prev + 1);
        setNewCourtName(`Court ${newCourtNumber + 1}`);
        setNewRtmpUrl('');
        setNewStreamKey('');
        setNewPlaybackUrl('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create court');
      }
    } catch (error) {
      toast.error('Error creating court stream');
    }
  };

  // Activate game on court
  const activateGame = async (courtStreamId, gameId) => {
    setActivatingGame(gameId);
    try {
      const res = await fetch(`${API_URL}/api/court-streams/${courtStreamId}/activate-game`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ game_id: gameId })
      });

      if (res.ok) {
        toast.success('Game activated on court');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to activate game');
      }
    } catch (error) {
      toast.error('Error activating game');
    } finally {
      setActivatingGame(null);
    }
  };

  // End current game on court
  const endGame = async (courtStreamId) => {
    try {
      const res = await fetch(`${API_URL}/api/court-streams/${courtStreamId}/end-game`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Game ended. Recording saved.`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to end game');
      }
    } catch (error) {
      toast.error('Error ending game');
    }
  };

  // Copy stream key to clipboard
  const copyStreamKey = (courtId, key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(courtId);
    toast.success('Stream key copied!');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Open edit dialog for a court
  const openEditCourt = (court) => {
    setEditCourtForm({
      court_name: court.court_name || '',
      rtmp_url: court.rtmps_url || '',
      stream_key: court.rtmps_stream_key || '',
      playback_url: court.playback_url || '',
    });
    setEditingCourt(court);
  };

  // Update court stream
  const updateCourtStream = async () => {
    if (!editingCourt) return;
    try {
      const res = await fetch(`${API_URL}/api/court-streams/${editingCourt.court_stream_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(editCourtForm)
      });
      if (res.ok) {
        toast.success('Court updated');
        setEditingCourt(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update court');
      }
    } catch {
      toast.error('Error updating court');
    }
  };

  // Delete court stream
  const deleteCourtStream = async (courtStreamId, courtName) => {
    if (!window.confirm(`Delete ${courtName}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/court-streams/${courtStreamId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        toast.success(`${courtName} deleted`);
        fetchData();
      } else {
        toast.error('Failed to delete court');
      }
    } catch {
      toast.error('Error deleting court');
    }
  };

  // Get available games (filter to this court's games by matching court_name to game.location)
  const getAvailableGames = (court) => {
    const activeGameIds = courts.map(c => c.current_game_id).filter(Boolean);
    return games
      .filter(g => 
        !activeGameIds.includes(g.game_id) && 
        g.status !== 'final' &&
        (g.location === court.court_name || g.court_name === court.court_name)
      )
      .sort((a, b) => {
        const tA = new Date(a.scheduled_at || a.scheduled_time || '9999');
        const tB = new Date(b.scheduled_at || b.scheduled_time || '9999');
        return tA - tB;
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Tournament Stream Setup</h3>
          <p className="text-sm text-zinc-400">
            Create one stream per court. All games on that court share the same stream.
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-rooam-red hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Court
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-700">
            <DialogHeader>
              <DialogTitle className="text-white">Add Court Stream</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a new stream key for a court. This key will be used for all games on this court.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="text-white">Court Name</Label>
                <Input
                  value={newCourtName}
                  onChange={(e) => setNewCourtName(e.target.value)}
                  placeholder="e.g., Court 1, Main Gym"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Court Number</Label>
                <Input
                  type="number"
                  value={newCourtNumber}
                  onChange={(e) => setNewCourtNumber(parseInt(e.target.value))}
                  min={1}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              {/* Platform Selection */}
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Streaming Platform</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'cloudflare', label: 'Cloudflare', color: 'orange' },
                    { id: 'custom', label: 'Custom', color: 'cyan' },
                    { id: 'youtube', label: 'YouTube', color: 'red' },
                    { id: 'twitch', label: 'Twitch', color: 'purple' },
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setNewPlatform(p.id)}
                      className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        newPlatform === p.id
                          ? `border-${p.color}-500 bg-${p.color}-950/40 text-${p.color}-400`
                          : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                      }`}
                      style={newPlatform === p.id ? {
                        borderColor: p.color === 'orange' ? '#f97316' : p.color === 'cyan' ? '#06b6d4' : p.color === 'red' ? '#ef4444' : '#a855f7',
                        color: p.color === 'orange' ? '#fb923c' : p.color === 'cyan' ? '#22d3ee' : p.color === 'red' ? '#f87171' : '#c084fc',
                        backgroundColor: p.color === 'orange' ? 'rgba(249,115,22,0.1)' : p.color === 'cyan' ? 'rgba(6,182,212,0.1)' : p.color === 'red' ? 'rgba(239,68,68,0.1)' : 'rgba(168,85,247,0.1)'
                      } : {}}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* RTMP Config (shown for custom/youtube/twitch) */}
              {newPlatform !== 'cloudflare' && (
                <div className="space-y-3 bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">RTMP URL</Label>
                    <Input
                      value={newRtmpUrl}
                      onChange={(e) => setNewRtmpUrl(e.target.value)}
                      placeholder={newPlatform === 'youtube' ? 'rtmp://a.rtmp.youtube.com/live2' : newPlatform === 'twitch' ? 'rtmp://live.twitch.tv/app' : 'rtmp://your-server/live'}
                      className="bg-zinc-900 border-zinc-600 text-white text-sm font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Stream Key</Label>
                    <Input
                      value={newStreamKey}
                      onChange={(e) => setNewStreamKey(e.target.value)}
                      placeholder="Your stream key"
                      className="bg-zinc-900 border-zinc-600 text-white text-sm font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Playback URL (HLS, optional)</Label>
                    <Input
                      value={newPlaybackUrl}
                      onChange={(e) => setNewPlaybackUrl(e.target.value)}
                      placeholder="https://... .m3u8"
                      className="bg-zinc-900 border-zinc-600 text-white text-sm font-mono"
                    />
                  </div>
                </div>
              )}

              <Button onClick={createCourtStream} className="w-full bg-rooam-red hover:bg-red-700">
                Create Court Stream
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Courts Grid */}
      {courts.length === 0 ? (
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Monitor className="w-12 h-12 text-zinc-500 mb-4" />
            <p className="text-zinc-400 text-center">
              No courts set up yet.<br />
              Add a court to get started with tournament streaming.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {courts.map((court) => {
            const currentGame = games.find(g => g.game_id === court.current_game_id);
            const availableGames = getAvailableGames(court);

            return (
              <Card 
                key={court.court_stream_id} 
                className={`bg-zinc-800/50 border-zinc-700 ${court.is_live ? 'ring-2 ring-red-500' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tv className="w-5 h-5 text-zinc-400" />
                      <CardTitle className="text-white text-lg">{court.court_name}</CardTitle>
                      {court.platform && court.platform !== 'cloudflare' && (
                        <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400 capitalize">
                          {court.platform}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {court.is_live ? (
                        <Badge className="bg-red-600">
                          <Radio className="w-3 h-3 mr-1 animate-pulse" />
                          LIVE
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                          Standby
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-white" onClick={() => openEditCourt(court)}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={() => deleteCourtStream(court.court_stream_id, court.court_name)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{getAvailableGames(court).length} games on this court</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stream Credentials */}
                  <div className="bg-zinc-900 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500">RTMP URL</span>
                      <span className="text-xs text-zinc-300 font-mono">{court.rtmps_url}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-zinc-500">Stream Key</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-300 font-mono">
                          ****{court.rtmps_stream_key?.slice(-8)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyStreamKey(court.court_stream_id, court.rtmps_stream_key)}
                          className="h-6 w-6 p-0"
                        >
                          {copiedKey === court.court_stream_id ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3 text-zinc-400" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Current Game */}
                  {currentGame ? (
                    <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-red-400 font-medium">NOW PLAYING</span>
                        <Badge className="bg-red-600/20 text-red-400 text-xs">
                          {currentGame.status}
                        </Badge>
                      </div>
                      <p className="text-white font-medium">
                        {currentGame.home_team_name} vs {currentGame.away_team_name}
                      </p>
                      <p className="text-sm text-zinc-400">
                        Score: {currentGame.score?.home || 0} - {currentGame.score?.away || 0}
                      </p>
                      <Button
                        onClick={() => endGame(court.court_stream_id)}
                        variant="outline"
                        size="sm"
                        className="w-full mt-3 border-red-700 text-red-400 hover:bg-red-900/20"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        End Game & Save Recording
                      </Button>
                    </div>
                  ) : (
                    /* Game Selector */
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-xs">Start Next Game</Label>
                      <Select
                        onValueChange={(gameId) => activateGame(court.court_stream_id, gameId)}
                        disabled={activatingGame !== null}
                        modal={false}
                      >
                        <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                          <SelectValue placeholder="Select a game to start..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700 z-[9999] max-h-60">
                          {availableGames.length === 0 ? (
                            <SelectItem value="_none" disabled className="text-zinc-500">
                              No games on this court
                            </SelectItem>
                          ) : (
                            availableGames.map((game) => {
                              const t = game.scheduled_at || game.scheduled_time;
                              const timeStr = t ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
                              return (
                                <SelectItem 
                                  key={game.game_id} 
                                  value={game.game_id}
                                  className="text-white hover:bg-zinc-800"
                                >
                                  {timeStr ? `${timeStr} — ` : ''}{game.home_team_name} vs {game.away_team_name}
                                </SelectItem>
                              );
                            })
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Instructions */}
      <Card className="bg-zinc-800/30 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">How Tournament Mode Works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-zinc-400 space-y-2">
          <p>1. <strong className="text-white">Create a court</strong> - Each court gets one stream key for the entire tournament.</p>
          <p>2. <strong className="text-white">Set up camera</strong> - Enter RTMP URL and stream key in your streaming app. Start streaming.</p>
          <p>3. <strong className="text-white">Activate games</strong> - Select which game is currently playing on each court.</p>
          <p>4. <strong className="text-white">End & switch</strong> - When a game ends, recording is saved. Select the next game.</p>
        </CardContent>
      </Card>
      {/* Edit Court Dialog */}
      <Dialog open={!!editingCourt} onOpenChange={(open) => { if (!open) setEditingCourt(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Court</DialogTitle>
            <DialogDescription className="text-zinc-400">Update court name and stream credentials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-white">Court Name</Label>
              <Input value={editCourtForm.court_name} onChange={(e) => setEditCourtForm({...editCourtForm, court_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">RTMP URL</Label>
              <Input value={editCourtForm.rtmp_url} onChange={(e) => setEditCourtForm({...editCourtForm, rtmp_url: e.target.value})} placeholder="rtmp://..." className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Stream Key</Label>
              <Input value={editCourtForm.stream_key} onChange={(e) => setEditCourtForm({...editCourtForm, stream_key: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Playback URL</Label>
              <Input value={editCourtForm.playback_url} onChange={(e) => setEditCourtForm({...editCourtForm, playback_url: e.target.value})} placeholder="https://... .m3u8" className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" />
            </div>
            <Button onClick={updateCourtStream} className="w-full bg-rooam-red hover:bg-red-700">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TournamentStreamManager;
